import React, { useEffect, useState, useCallback } from "react";
import get from "lodash/get";
import { Button, Modal } from "react-bootstrap";
import Select from "react-select";
import textUtils from "../../../common/utilities/TextUtils";
import { TABLE_ATTRIBUTE_TYPES } from "../../../../settings/constants/types";
import { toast } from "react-toastify";
import { getDataTypes, DataTypeConfigView } from "../../../../utils/datatypes/DataTypeConfig";
import ObjectField from '../../../../utils/datatypes/typeimpl/ObjectField.js';

const INITIAL_STATE = {
    id: null,
    name: "",
    type: "",
    comment: "",
    listAttribute: false,
};

// Helper function to find nested attributes
const findNestedAttributes = (attributes, path) => {
    if (!path.length) return attributes;
    const [current, ...rest] = path;
    const currentAttr = attributes[current];
    return rest.length ? findNestedAttributes(currentAttr.attributes, rest) : currentAttr.attributes;
};

// Helper function to get attribute path names
const getAttributePathNames = (attributes, path) => {
    if (!path.length) return [];
    const [current, ...rest] = path;
    const currentAttr = attributes[current];
    return [
        { index: current, name: currentAttr.name || `Attribute ${current + 1}` },
        ...getAttributePathNames(currentAttr.attributes, rest)
    ];
};

// Helper function to update nested attributes
const updateNestedAttributes = (attributes, path, newAttributes) => {
    if (!path.length) return newAttributes;
    const [current, ...rest] = path;
    const newArray = [...attributes];
    if (rest.length) {
        newArray[current] = {
            ...newArray[current],
            attributes: updateNestedAttributes(newArray[current].attributes, rest, newAttributes)
        };
    } else {
        newArray[current] = {
            ...newArray[current],
            attributes: newAttributes
        };
    }
    return newArray;
};

// Helper function to validate attribute name
const isValidAttributeName = (name) => {
    return name && name.trim().length > 0;
};

const BreadcrumbNav = ({ pathNames, onNavigate }) => {
    return (
        <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-3">
                <li className="breadcrumb-item">
                    <Button
                        variant="link"
                        className="p-0"
                        onClick={() => onNavigate([])}
                    >
                        Root
                    </Button>
                </li>
                {pathNames.map((item, index) => (
                    <li key={index} className="breadcrumb-item">
                        <Button
                            variant="link"
                            className="p-0"
                            onClick={() => onNavigate(pathNames.slice(0, index + 1).map(p => p.index))}
                        >
                            {item.name}
                        </Button>
                    </li>
                ))}
            </ol>
        </nav>
    );
};

const AttributeEditor = ({ attributes = [], onChange, currentPath = [], onPathChange }) => {
    const [currentAttributes, setCurrentAttributes] = useState([]);
    const [pathNames, setPathNames] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);

    // Load type options from API
    useEffect(() => {
        const loadTypeOptions = async () => {
            setIsLoadingTypes(true);
            try {
                const types = await getDataTypes();
                setTypeOptions(types);
            } catch (error) {
                console.error('Error loading data types:', error);
            } finally {
                setIsLoadingTypes(false);
            }
        };
        loadTypeOptions();
    }, []);

    useEffect(() => {
        const attrs = findNestedAttributes(attributes || [], currentPath) || [];
        const names = getAttributePathNames(attributes || [], currentPath);
        setCurrentAttributes(attrs);
        setPathNames(names);
    }, [attributes, currentPath]);

    const handleAddAttribute = () => {
        const newAttributes = [...currentAttributes, { 
            name: "", 
            type: "", 
            comment: "", 
            attributes: [],
            listAttribute: false
        }];
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleRemoveAttribute = (index) => {
        const newAttributes = [...currentAttributes];
        newAttributes.splice(index, 1);
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleAttributeChange = (index, field, value) => {
        const newAttributes = [...currentAttributes];
        newAttributes[index] = { ...newAttributes[index], [field]: value };
        
        // If type is changed to OBJECT, initialize config.attributes
        if (field === 'type' && value === TABLE_ATTRIBUTE_TYPES.OBJECT) {
            if (!newAttributes[index].config) {
                newAttributes[index].config = {};
            }
            newAttributes[index].config.attributes = newAttributes[index].attributes || [];
        }
        // If type is changed from OBJECT, move attributes back to direct attributes
        else if (field === 'type' && value !== TABLE_ATTRIBUTE_TYPES.OBJECT) {
            if (newAttributes[index].config?.attributes) {
                newAttributes[index].attributes = newAttributes[index].config.attributes;
            }
            delete newAttributes[index].config;
        }
        
        onChange(updateNestedAttributes(attributes || [], currentPath, newAttributes));
    };

    const handleNavigateToNested = (index) => {
        onPathChange([...currentPath, index]);
    };

    // More defensive check for canAddAttribute
    const canAddAttribute = Array.isArray(currentAttributes) && 
        (currentAttributes.length === 0 || currentAttributes.every(attr => attr && isValidAttributeName(attr.name)));

    return (
        <div>
            {currentPath.length > 0 && (
                <BreadcrumbNav
                    pathNames={pathNames}
                    onNavigate={onPathChange}
                />
            )}
            <div className="mb-3">
                {Array.isArray(currentAttributes) && currentAttributes.map((attr, index) => (
                    <div key={index} className="mb-3 p-2 border rounded">
                        <div className="row mb-2">
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className={`form-control ${!isValidAttributeName(attr?.name) ? 'is-invalid' : ''}`}
                                    placeholder="Attribute Name"
                                    value={attr?.name || ""}
                                    onChange={(e) => handleAttributeChange(index, "name", e.target.value)}
                                />
                                {!isValidAttributeName(attr?.name) && (
                                    <div className="invalid-feedback">
                                        Attribute name is required
                                    </div>
                                )}
                            </div>
                            <div className="col-md-4">
                                <Select
                                    options={typeOptions}
                                    value={typeOptions.find(option => option.value === attr?.type) || null}
                                    onChange={(e) => handleAttributeChange(index, "type", e?.value)}
                                    isLoading={isLoadingTypes}
                                    placeholder="Select Type"
                                    className="react-select-container"
                                />
                            </div>
                            <div className="col-md-2">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`listAttribute-${index}`}
                                        checked={attr?.listAttribute || false}
                                        onChange={(e) => handleAttributeChange(index, "listAttribute", e.target.checked)}
                                    />
                                    <label className="form-check-label small" htmlFor={`listAttribute-${index}`}>
                                        List
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleRemoveAttribute(index)}
                                >
                                    Remove
                                </Button>
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-md-8">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Comment (optional)"
                                    value={attr?.comment || ""}
                                    onChange={(e) => handleAttributeChange(index, "comment", e.target.value)}
                                />
                            </div>
                        </div>
                        {(attr?.type === TABLE_ATTRIBUTE_TYPES.OBJECT) && (
                            <div className="mt-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleNavigateToNested(index)}
                                    disabled={!isValidAttributeName(attr?.name)}
                                >
                                    Edit Nested Attributes ({(attr?.config?.attributes || attr?.attributes || []).length})
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
                <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={handleAddAttribute}
                    disabled={!canAddAttribute}
                >
                    Add Attribute
                </Button>
            </div>
        </div>
    );
};

const AddResponseParameter = ({
    isOpen = false,
    row = null,
    onSuccessCallBack,
    onClose,
}) => {
    const [parameterObj, setParameterObj] = useState({ ...INITIAL_STATE });
    const [parameterTypeKeyValue, setParameterTypeKeyValue] = useState(null);
    const [currentPath, setCurrentPath] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    const [configData, setConfigData] = useState({});

    // Load type options from API
    useEffect(() => {
        const loadTypeOptions = async () => {
            setIsLoadingTypes(true);
            try {
                const types = await getDataTypes();
                setTypeOptions(types);
            } catch (error) {
                console.error('Error loading data types:', error);
            } finally {
                setIsLoadingTypes(false);
            }
        };
        loadTypeOptions();
    }, []);

    useEffect(() => {
        if (isOpen) {
            if (row) {
                console.log('Loading response parameter for edit:', row);
                setParameterObj({
                    id: row.id,
                    name: get(row, "name", ""),
                    type: get(row, "type", ""),
                    comment: get(row, "comment", ""),
                    attributes: get(row, "attributes", []),
                    listAttribute: get(row, "listAttribute", false),
                });
                setParameterTypeKeyValue(typeOptions.find(option => option.value === row.type) || null);
                
                // Load existing config data if available
                if (row.config) {
                    console.log('Loading config data:', row.config);
                    setConfigData(row.config);
                    // For OBJECT types, also load nested attributes from config
                    if (row.type === TABLE_ATTRIBUTE_TYPES.OBJECT && row.config.attributes) {
                        console.log('Loading OBJECT attributes from config:', row.config.attributes);
                        setParameterObj(prev => ({
                            ...prev,
                            attributes: row.config.attributes
                        }));
                    }
                } else {
                    // For OBJECT types without config, initialize empty configData with attributes
                    if (row.type === TABLE_ATTRIBUTE_TYPES.OBJECT) {
                        setConfigData({
                            attributes: get(row, "attributes", [])
                        });
                    } else {
                        setConfigData({});
                    }
                }
            } else {
                setParameterObj({ ...INITIAL_STATE });
                setParameterTypeKeyValue(null);
                setConfigData({});
            }
            setCurrentPath([]);
        }
    }, [isOpen, row, typeOptions]);

    const handleTextChange = (key, value) => {
        setParameterObj((prev) => ({ ...prev, [key]: value }));
        
        // Clear config data when type changes, but preserve attributes for OBJECT type
        if (key === 'type') {
            if (value === TABLE_ATTRIBUTE_TYPES.OBJECT) {
                // For OBJECT type, preserve existing attributes in configData
                setConfigData(prev => ({
                    ...prev,
                    attributes: prev.attributes || parameterObj.attributes || []
                }));
            } else {
                setConfigData({});
            }
        }
    };

    const handleConfigChange = useCallback((fieldName, value) => {
        console.log('Config change in AddResponseParameter:', fieldName, value);
        setConfigData(prev => {
            const newConfig = {
                ...prev,
                [fieldName]: value
            };
            console.log('Updated configData:', newConfig);
            return newConfig;
        });
        
        // For OBJECT types, also update parameterObj.attributes when attributes change
        if (parameterObj.type === TABLE_ATTRIBUTE_TYPES.OBJECT && fieldName === 'attributes') {
            setParameterObj(prev => ({
                ...prev,
                attributes: value
            }));
        }
    }, [parameterObj.type]);

    const handleSaveParameter = () => {
        if (
            textUtils.isEmpty(parameterObj.name, "Name", true) ||
            textUtils.isEmpty(parameterObj.type, "Type", true)
        ) {
            return false;
        }

        // Validate attributes if type is OBJECT 
        if ((parameterObj.type === TABLE_ATTRIBUTE_TYPES.OBJECT) &&
            (!parameterObj.attributes || parameterObj.attributes.length === 0)) {
            toast.error("Please add at least one attribute for the object");
            return false;
        }

        // Validate all attribute names and nested objects
        const validateAttributes = (attrs) => {
            if (!Array.isArray(attrs)) return true;
            
            return attrs.every(attr => {
                if (!attr) return true;

                // Check if attribute name is valid
                if (!isValidAttributeName(attr.name)) {
                    toast.error(`Attribute "${attr.name || 'Unnamed'}" must have a valid name`);
                    return false;
                }

                // Check if nested objects have at least one inner attribute
                if ((attr.type === TABLE_ATTRIBUTE_TYPES.OBJECT)) {
                    const nestedAttributes = attr.config?.attributes || attr.attributes;
                    if (!nestedAttributes || nestedAttributes.length === 0) {
                        toast.error(`Nested object "${attr.name}" must have at least one inner attribute`);
                        return false;
                    }
                    // Recursively validate nested attributes
                    return validateAttributes(nestedAttributes);
                }

                return true;
            });
        };

        // Always validate attributes if they exist, regardless of the main type
        if (parameterObj.attributes && parameterObj.attributes.length > 0) {
            if (!validateAttributes(parameterObj.attributes)) {
                return false;
            }
        }

        // Prepare the parameter object for saving
        const parameterToSave = { ...parameterObj };
        
        // For OBJECT types, move attributes to config.attributes
        if (parameterObj.type === TABLE_ATTRIBUTE_TYPES.OBJECT) {
            if (parameterObj.attributes && parameterObj.attributes.length > 0) {
                const cleanAttributes = (attrs) => {
                    if (!Array.isArray(attrs)) return attrs;
                    return attrs.map(attr => {
                        if (!attr) return attr;
                        const cleanedAttr = { ...attr };
                        if (attr.type !== TABLE_ATTRIBUTE_TYPES.OBJECT) {
                            // For non-object attributes, ensure no direct attributes array is persisted
                            delete cleanedAttr.attributes;
                            // Preserve config for non-object types (e.g., ENUM may need enumValues)
                        } else {
                            // For nested OBJECT types, move attributes to config.attributes
                            const nestedAttributes = attr.config?.attributes || attr.attributes;
                            if (nestedAttributes && nestedAttributes.length > 0) {
                                if (!cleanedAttr.config) {
                                    cleanedAttr.config = {};
                                }
                                cleanedAttr.config.attributes = cleanAttributes(nestedAttributes);
                            }
                            delete cleanedAttr.attributes;
                        }
                        return cleanedAttr;
                    });
                };
                
                // Initialize config if it doesn't exist
                if (!parameterToSave.config) {
                    parameterToSave.config = {};
                }
                const cleanedAttributes = cleanAttributes(parameterObj.attributes);
                console.log('Saving OBJECT attributes to config:', cleanedAttributes);
                parameterToSave.config.attributes = cleanedAttributes;
            }
            // Remove attributes from root level for OBJECT types
            delete parameterToSave.attributes;
        } else {
            // Remove attributes key if type is not OBJECT
            delete parameterToSave.attributes;
        }

        // Add config data to the parameter object
        if (Object.keys(configData).length > 0) {
            parameterToSave.config = { ...parameterToSave.config, ...configData };
        }

        if (!(parameterObj?.id?.length > 0)) {
            delete parameterToSave.id;
        }
        
        console.log('Saving response parameter:', parameterToSave);
        onSuccessCallBack(parameterToSave);
        onClose();
    };

    return (
        <Modal size="lg" show={isOpen} onHide={onClose} className="add-response-parameter-modal add-more-form">
            <Modal.Header closeButton>
                <Modal.Title>{`${parameterObj?.id ? "Edit" : "Add"} Response Parameter`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="form-group row mb-3">
                    <div className="col-lg-12">
                        <label>Name *</label>
                        <input
                            type="text"
                            className="form-control input-default"
                            placeholder="Name"
                            onChange={(e) => handleTextChange("name", e.target.value)}
                            value={parameterObj.name}
                        />
                    </div>
                </div>
                <div className="form-group row mb-3">
                    <div className="col-lg-12">
                        <label>Type *</label>
                        <Select
                            options={typeOptions}
                            value={parameterTypeKeyValue}
                            onChange={(selected) => {
                                setParameterTypeKeyValue(selected);
                                handleTextChange("type", selected?.value || "");
                            }}
                            isLoading={isLoadingTypes}
                            placeholder="Select Type"
                            className="react-select-container"
                        />
                    </div>
                </div>
                <div className="form-group row mb-3">
                    <div className="col-lg-12 text-left">
                        <label className=''>List Attribute</label>
                        <input
                            type="checkbox"
                            name="listAttribute"
                            id="listAttribute"
                            checked={parameterObj.listAttribute}
                            onChange={(e) => handleTextChange("listAttribute", e.target.checked)}
                            style={{
                                display: 'block',
                                width:'max-content',
                                marginLeft:'5px'
                            }}
                        />
                    </div>
                </div>
                
                {/* Data Type Configuration */}
                {parameterTypeKeyValue && (
                    <>
                        {console.log('Rendering DataTypeConfigView with:', {
                            selectedDataType: parameterTypeKeyValue,
                            configData: configData,
                            parameterObjId: parameterObj.id
                        })}
                        <DataTypeConfigView
                            selectedDataType={parameterTypeKeyValue}
                            configData={configData}
                            onConfigChange={handleConfigChange}
                        />
                    </>
                )}
                
                <div className="form-group row mb-3">
                    <div className="col-lg-12">
                        <label>Comment </label>
                        <input
                            type="text"
                            className="form-control input-default"
                            placeholder="Comment"
                            onChange={(e) => handleTextChange("comment", e.target.value)}
                            value={parameterObj.comment}
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={handleSaveParameter}>
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddResponseParameter;
